import { Injectable } from '@angular/core';
import { lmCardViewFormatter } from '@list/formatters/lm-card-view.formatter';
import { LMListCardService } from './lm-list-card.service';
import { LMListGridPagingService } from './lm-list-grid-paging.service';
import { LMFormatUtilService } from '@services/utils';
import { LMContextService } from '@services/index';
import { Subject } from 'rxjs';
import { LMListEventService } from './lm-list-event.service';

@Injectable()
export class LMListHierarchyService {

  private index: number = null;
  private flatDataViewArray: Array<LMDataView> = [];
  private centeredIndex = null;
  private implementsCancelablePaging = false;
  private cancelPagingNotifier$: Subject<boolean>;
  public lmListEventService: LMListEventService;

  constructor(
    private lmListCardService: LMListCardService,
    private lmListGridPagingService: LMListGridPagingService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmContextService: LMContextService,
  ) {
    this.implementsCancelablePaging = this.lmContextService.isUiPagingRequestWaitTimeEnabled;
  }

  public handleCancellableRequest(): void {
    if (this.implementsCancelablePaging && this.cancelPagingNotifier$) {
      this.cancelPagingNotifier$.next(true);
      this.cancelPagingNotifier$.complete();
      this.cancelPagingNotifier$ = null;
    }
  }

  public handleCancelableResponse(response: LMListPagingResponse): boolean {
    if (this.implementsCancelablePaging  && this.lmListEventService) {
      if (response.status === 'STARTED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'started', cancelablePagingNotifier: response.cancelablePagingNotifier });
        this.cancelPagingNotifier$ = response.cancelablePagingNotifier;
        return true;
      } else if (response.status === 'WAITING') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'waiting' });
        return true;
      } else if (response.status === 'CANCELED') {
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'canceled' });
        // this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
        return true;
      } else {
        this.cancelPagingNotifier$ = null;
        this.lmListEventService.cancelablePagingEvent(<LMCancelablePagingEvent>{ state: 'finished' });
        return false;
      }
    } else {
      return false;
    }
  }

  public getFlatDataViewArray(): Array<LMDataView> {
    return this.flatDataViewArray;
  }

  public getCenteredNodeIndex(): number {
    return this.centeredIndex;
  }

  public async loadData(
    listSpec: LMListSpec,
    data: Array<LMDataView>,
    legendKey: string,
    centeredNode?: LMDataView,
    loadChildren?: boolean,
    filter?: boolean
  ): Promise<Array<SohoStackedHierarchyData<LMHierarchyDataView>>> {
    if (centeredNode === undefined) {
      return;
    }

    // New page each time data is loaded, reset indexes and flat data view array
    this.reset();

    const hierarchyData = {
      ancestorPath: null,
      centeredNode: null,
      children: undefined
    };

    // Get ancestors
    if (centeredNode !== null && centeredNode.positionToUrl && !filter) {
      hierarchyData.ancestorPath = await this.getData(centeredNode.positionToUrl, listSpec, legendKey, true);
    }

    // Set center node after ancestors, ensures ids are in right order
    if (centeredNode !== null && !filter) {
      hierarchyData.centeredNode = Object.assign({}, centeredNode, this.getSohoStackedHierarchyData(listSpec, centeredNode));
      this.centeredIndex = this.index;

      hierarchyData['menu'] = {
        details: this.createDetailFieldsArray(listSpec, centeredNode),
        actions: []
      };
    }

    // Get children
    if (loadChildren && centeredNode.childrenUrl) {

      // todo: ppatton khollenbeck use listSpec.childrenUrl and listSpec.centeredUrl url templates
      // todo: how to deal with fromDataView here?
      // const url = compiledUrlTemplate.expand({ resourceId: (this.fromDataView) ? this.fromDataView.resourceId : undefined});
      // const compiledUrlTemplate: any = urlTemplate.parse(listSpec.childrenUrl);
      // const childrenUrl = compiledUrlTemplate.expand({ resourceId: centeredNode.resourceId });

      const childrenUrl = centeredNode.childrenUrl;
      hierarchyData.children = await this.getData(childrenUrl, listSpec, legendKey);
    } else {
      hierarchyData.children = this.formatDataForTemplate(listSpec, data, legendKey);
    }

    if (legendKey && (hierarchyData.centeredNode && hierarchyData.centeredNode.fields)) {
      hierarchyData.centeredNode[legendKey] = hierarchyData.centeredNode.fields[legendKey].value.toString();
    }

    return filter && hierarchyData.children.length === 0 ? [] : [ hierarchyData ];
  }

  private async getData(
    url: string,
    listSpec: LMListSpec,
    legendKey: string,
    returnAncestors?: boolean
  ): Promise<Array<LMHierarchyDataView>> {
    return new Promise((resolve, reject) => {
      this.handleCancellableRequest();
      const pageSize = listSpec.pageSize ? listSpec.pageSize : 20;
      this.lmListGridPagingService
        .initialPageFiltered(url, undefined, pageSize, undefined)
        .subscribe(
          (listPagingResponse: LMListPagingResponse) => {

            if (this.handleCancelableResponse(listPagingResponse)) {
              return;
            }

            const data = returnAncestors ? this.getAncestorPath(listPagingResponse, legendKey) : listPagingResponse.dataViewSet.data;
            resolve(this.formatDataForTemplate(listSpec, data, legendKey));
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Strips out special characters for prefix
   * @param str
   */
  public getPrefix(str: string): string {
    const id = str;
    const excludeChars = [
      '!', '@', '#', '$', '%', '^', '&', '*', '(', ')', '=',
      '+', '\\', '{', '}', ']', '[', '<', '>', '?', '/', '.',
      ',', '"', ':', ';'
    ];

    const prefix = [];
    id.split('').forEach((char) => {
      if (!excludeChars.includes(char)) {
        prefix.push(char);
      }
    });

    return prefix.join('');
  }

  private formatDataForTemplate(
    listSpec: LMListSpec,
    dataViews: Array<LMDataView>,
    legendKey: string
  ): Array<LMHierarchyDataView> {
    const updatedDataViews = dataViews.map((data: LMDataView) => {
      const templateData = this.getSohoStackedHierarchyData(listSpec, data);

      if (legendKey && data.fields) {
        templateData[legendKey] = data.fields[legendKey].value.toString();
      }

      return templateData;
    });

    return updatedDataViews;
  }

  private getAncestorPath(listPagingResponse: LMListPagingResponse, legendKey: string): Array<LMDataView> {
    const ancestorPathArray = listPagingResponse.dataViewSet.positionToDataView;

    // Drop the last item. This is a duplicate of the centered node
    ancestorPathArray.pop();

    const updatedArray = ancestorPathArray.map((data) => {
      if (legendKey && data.fields) {
        data[legendKey] = data.fields[legendKey].value.toString();
      }

      return data;
    });

    return updatedArray;
  }

  private getSohoStackedHierarchyData(listSpec: LMListSpec, dataView: LMDataView): LMHierarchyDataView {
    const displayField: LMListDisplayField = listSpec.busListSpec.orgChartCardView;
    const cardInfo = lmCardViewFormatter.getCardTemplateData(this.lmListCardService, displayField, dataView);
    const columns = cardInfo.columns;
    const firstColumn = columns[0];

    /**
     *  Hierarchy card only displays the left(image), and middle column
     *  Hierarchy card only shows up to three rows in the middle column
     *  Hierarchy card does not show the right column
     *  Extra information is displayed in detail fields
     */
    const rowOne = firstColumn.column[0];
    const rowTwo = firstColumn.column[1];
    const rowThree = firstColumn.column[2];

    // Push all dataViews to single array
    this.flatDataViewArray.push(dataView);

    return {
      id: this.getId(),
      resourceId: dataView.resourceId,
      imageUrl: cardInfo.imageUrl,
      imageTitle: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ImageAltText'), firstColumn && rowOne ? rowOne.displayValue : ''),
      randomBackground: displayField.leftColumn ? displayField.leftColumn.isRandomBackground : null,
      missingImage: cardInfo.isMissingImage,
      foregroundText: cardInfo.imageForegroundText,
      subheading: firstColumn && rowTwo ? rowTwo.displayValue : '',
      micro: firstColumn && rowThree ? rowThree.displayValue : '',
      title: firstColumn && rowOne ? rowOne.displayValue : '',
      isLeaf: dataView.isLeaf,
      bgClass: cardInfo.bgColorClass,
      doesNotExist: dataView.doesNotExist,
      menu: {
        details: this.createDetailFieldsArray(listSpec, dataView),
        actions: []
      }
    };
  }

  private createDetailFieldsArray(listSpec: LMListSpec, dataView: LMDataView): Array<LMHierarchyDetailField> {
    const detailFields = [];

    if (listSpec.busListSpec.detailFields) {
      listSpec.busListSpec.detailFields.forEach((field) => {
        const detailField: LMHierarchyDetailField = {};

        // Ignore image field
        if (field.displayAsImage) {
          return;
        }

        // dataView.fields[field.fieldId]
        if (field.label) {
          detailField.key = this.lmFormatUtilService.formatMessageRule(field.label, dataView);
        } else {
          detailField.key = '';
        }

        // if label is not defined set they value as the label
        if ((dataView && dataView.fields) && dataView.fields[field.fieldId]) {
          detailField.value = this.lmFormatUtilService.toDisplayValue(dataView.fields[field.fieldId].value, field.type);
        } else {
          detailField.value = '';
        }

        // Skip if both label and value are empty
        if (field.label === undefined && detailField.value.length === 0) {
          return;
        }

        detailFields.push(detailField);
      });
    }

    return detailFields;
  }

  private reset(): void {
    this.flatDataViewArray = [];
    this.centeredIndex = null;
    this.index = null;
  }

  private getId(): string {
    if (this.index === null) {
      this.index = 0;
    } else {
      this.index++;
    }

    return this.index.toString();
  }
}
